import GlobalCallbackLogsService from 'Main/service/GlobalCallbackLogsService'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getRefreshToken } from 'variables/common'
import { GlobalCallbackLogModal } from './Modal/GlobalCallbackLogModal'
import JsonViewerPanel from 'views/components/JsonViewer/JsonViewerPanel'
import { CatchError } from 'Exceptions/exceptions'

function GlobalCallbackLogDetails () {
  const { id } = useParams()
  const navigate = useNavigate()
  const refreshToken = getRefreshToken()
  const globalCallbackLogsService = useMemo(() => new GlobalCallbackLogsService(), [])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [log, setLog] = useState(null)

  const [resending, setResending] = useState(false)
  const [resendResult, setResendResult] = useState(null) // { ok: bool, message: string }

  const fetchLog = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      const res = await globalCallbackLogsService.getById(id)
      setLog(res.data)
    } catch (err) {
      const caught = CatchError(err, navigate, refreshToken)
      if (caught !== undefined) setError('Failed to load callback log entry.')
    } finally {
      setLoading(false)
    }
  }, [id, navigate, globalCallbackLogsService, refreshToken])

  useEffect(() => {
    fetchLog()
  }, [fetchLog])

  const handleResend = async () => {
    setResending(true)
    setResendResult(null)
    try {
      await globalCallbackLogsService.resend(id)
      setResendResult({ ok: true, message: 'Resend callback Success: refreshing...' })
      await fetchLog()
    } catch (err) {
      const caught = CatchError(err, navigate, refreshToken)
      if (caught !== undefined) {
        setResendResult({ ok: false, message: err?.message || 'Resend Callback failed' })
      }
    } finally {
      setResending(false)
    }
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="d-flex align-items-center gap-2 py-5 justify-content-center">
          <i className="fa fa-circle-o-notch fa-spin" />
          <span>Loading callback log...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <a href="/dashboard/global-callback-logs" className="btn btn-light text-black mb-3">
          <i className="fa-solid fa-arrow-left" /> Back
        </a>
        <div className="alert alert-danger">
          <i className="fa fa-exclamation-circle me-1" />
          {error}
        </div>
      </div>
    )
  }

  if (!log) return null

  return (
    <>
      <div className="page-heading">
        <div className="row align-items-center">
          <div className="col-12 col-md-6">
            <h3>Callback Log Details</h3>
            <p className="text-subtitle text-muted mb-0">
              Log ID: <code>{log.id}</code>
            </p>
          </div>
          <div className="col-12 col-md-6 text-md-end mt-2 mt-md-0">
            <button type="button" className="btn btn-primary me-2" disabled={resending} onClick={handleResend}>
              <i className={`fa-solid ${resending ? 'fa-circle-o-notch fa-spin' : 'fa-rotate-right'}`} />{' '}
              {resending ? 'Processing...' : 'Resend Callback'}
            </button>
            <a href="/dashboard/global-callback-logs" className="btn btn-light text-black">
              <i className="fa-solid fa-arrow-left" /> Back
            </a>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="row justify-content-center">
          <div className="col-12">
            {resendResult && (
              <div className={`alert ${resendResult.ok ? 'alert-success' : 'alert-danger'}`} role="alert">
                <strong>{resendResult.message}</strong>
              </div>
            )}

            <GlobalCallbackLogModal log={log} />

            <div className="row">
              <div className="col-12 col-lg-6">
                <JsonViewerPanel data={log.payload} title="Request Payload" />
              </div>
              <div className="col-12 col-lg-6">
                <JsonViewerPanel data={log.endpoint_response} title="Response Payload" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default GlobalCallbackLogDetails
